/**
 * Timestamp coercion for roadmap data.
 *
 * Roadmap dates reach the admin app in three different shapes depending on the
 * transport, and shared-types declares all of them as `Date`:
 *
 *   - `listRoadmapItems` (callable)  → `{_seconds, _nanoseconds}`, because the
 *     callable protocol JSON-serialises Admin-SDK Timestamps.
 *   - `roadmap/{id}/comments` (direct Firestore read) → a real client-SDK
 *     `Timestamp`, which has `.toDate()`.
 *   - Anything already normalised upstream → a `Date`.
 *
 * `toDateSafe` accepts `unknown` and returns `null` for anything it can't make
 * sense of, so a shape we haven't seen degrades to '—' instead of throwing.
 */
import { formatDistanceToNow } from 'date-fns';

/** Narrow an unknown value to a `Date`, or `null` when it isn't one. */
export function toDateSafe(value: unknown): Date | null {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  // Client-SDK Timestamp (direct Firestore reads).
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    const maybe = (value as { toDate: unknown }).toDate;
    if (typeof maybe === 'function') {
      try {
        const date = (value as { toDate: () => Date }).toDate();
        return date instanceof Date && !Number.isNaN(date.getTime())
          ? date
          : null;
      } catch {
        return null;
      }
    }
  }

  // Serialised Timestamp over the callable wire. The Admin SDK emits
  // `_seconds`; some paths emit the un-prefixed `seconds` — accept both.
  if (typeof value === 'object' && value !== null) {
    const record = value as Record<string, unknown>;
    const seconds = record._seconds ?? record.seconds;
    if (typeof seconds === 'number' && Number.isFinite(seconds)) {
      const date = new Date(seconds * 1000);
      return Number.isNaN(date.getTime()) ? null : date;
    }
  }

  // ISO string or epoch millis.
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

/** Relative time ('3 days ago') for grid cells, or '—' when unparseable. */
export function formatRelative(value: unknown): string {
  const date = toDateSafe(value);
  if (!date) return '—';
  return `${formatDistanceToNow(date)} ago`;
}

/** Absolute locale date-time for modal read-only fields and comment stamps. */
export function formatAbsolute(value: unknown): string {
  const date = toDateSafe(value);
  if (!date) return '—';
  return date.toLocaleString();
}

/** Sort key: epoch millis, with unparseable dates sorting last on `desc`. */
export function toSortMillis(value: unknown): number {
  const date = toDateSafe(value);
  return date ? date.getTime() : 0;
}
